import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '../Icons';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';

const EASE    = [0.22, 1, 0.36, 1];
const SANS    = '"Geist", "Inter", system-ui, sans-serif';
const MONO    = '"Geist Mono", ui-monospace, monospace';
const BRAND   = '#5A38FD';
const BRAND_2 = '#7F00FF';
const BRAND_L = '#a78bfa';
const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`;

// Risk score colors — semantic: red = danger, amber = caution, green = safe (risk spectrum, not earnings)
const scoreColor = (s) => {
  if (s >= 80) return { text: '#4ade80', bg: 'rgba(74,222,128,0.10)', border: 'rgba(74,222,128,0.22)', label: 'Excellent' };
  if (s >= 60) return { text: '#fbbf24', bg: 'rgba(251,191,36,0.10)',  border: 'rgba(251,191,36,0.22)',  label: 'Good'      };
  if (s >= 40) return { text: '#fb923c', bg: 'rgba(249,115,22,0.10)',  border: 'rgba(249,115,22,0.22)',  label: 'Fair'      };
  return           { text: '#f87171', bg: 'rgba(248,113,113,0.10)', border: 'rgba(248,113,113,0.22)', label: 'Poor'      };
};

const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const _extractAmount = (summary, keyTerms) => {
  try {
    const text = String(summary || '') + ' ' + (Array.isArray(keyTerms) ? keyTerms.map(k => k.description).join(' ') : '');
    const m = text.match(/\$\s?([0-9][0-9,.]+)/);
    return m ? parseFloat(m[1].replace(/,/g, '')) : null;
  } catch { return null; }
};

const _setGhostContext = (analysisObj, title) => {
  try {
    localStorage.setItem('analyzer_context', JSON.stringify({
      safety_score: analysisObj?.safety_score,
      red_flags:   (analysisObj?.red_flags || []).slice(0, 10),
      key_terms:   (analysisObj?.key_terms || []).slice(0, 12),
      deal_title:  title || 'NIL Deal',
    }));
  } catch {}
};

// ─── Input style shared ───────────────────────────────────────────────────────
const INPUT = {
  width: '100%', boxSizing: 'border-box',
  padding: '10px 13px', borderRadius: 9,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#fff', fontSize: '0.875rem',
  outline: 'none', fontFamily: SANS,
  transition: 'border-color 160ms, background 160ms',
};

// ─── Button helpers ───────────────────────────────────────────────────────────
const BtnPrimary = ({ children, onClick, disabled, style = {} }) => (
  <motion.button whileTap={{ scale: 0.97 }} onClick={onClick} disabled={disabled}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, border: 'none',
      background: disabled ? 'rgba(90,56,253,0.35)' : `linear-gradient(135deg, ${BRAND}, ${BRAND_2})`,
      color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: SANS, boxShadow: disabled ? 'none' : '0 4px 14px rgba(90,56,253,0.35)',
      whiteSpace: 'nowrap', flexShrink: 0, ...style }}>
    {children}
  </motion.button>
);

const BtnGhost = ({ children, onClick, danger, style = {} }) => (
  <motion.button whileTap={{ scale: 0.97 }} onClick={onClick}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10,
      background: danger ? 'rgba(248,113,113,0.07)' : 'rgba(255,255,255,0.04)',
      border: `1px solid ${danger ? 'rgba(248,113,113,0.22)' : 'rgba(255,255,255,0.1)'}`,
      color: danger ? '#f87171' : 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: '0.82rem',
      cursor: 'pointer', fontFamily: SANS, whiteSpace: 'nowrap', flexShrink: 0, ...style }}>
    {children}
  </motion.button>
);

// ─── Score Ring ───────────────────────────────────────────────────────────────
function ScoreRing({ score, color, size = 110 }) {
  const r = 40;
  const circ = 2 * Math.PI * r;
  const dash = ((Math.min(score ?? 0, 100)) / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 96 96" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="48" cy="48" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="7" />
        <motion.circle cx="48" cy="48" r={r} fill="none" stroke={color} strokeWidth="7"
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ - dash }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.15 }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <span style={{ fontFamily: MONO, fontSize: '1.5rem', fontWeight: 800, color, lineHeight: 1, letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums' }}>{score ?? '—'}</span>
        <span style={{ fontSize: '0.52rem', color: 'rgba(255,255,255,0.25)', fontFamily: MONO, textTransform: 'uppercase', letterSpacing: '0.08em' }}>/100</span>
      </div>
    </div>
  );
}

// ─── Analyzing state ──────────────────────────────────────────────────────────
function AnalyzingState() {
  const [stage, setStage] = useState(0);
  const stages = ['Reading document…', 'Extracting key terms…', 'Detecting red flags…', 'Calculating safety score…'];
  useEffect(() => {
    const t = setInterval(() => setStage(s => (s + 1) % stages.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28, padding: '48px 24px', fontFamily: SANS }}>
      <style>{`
        @keyframes scanDoc { 0% { top: 16px; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { top: 84px; opacity: 0; } }
        @keyframes caOrbit { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.92); } }
      `}</style>
      <div style={{ position: 'relative', width: 80, height: 100 }}>
        <div style={{ width: 80, height: 100, borderRadius: 8, background: '#1A1A2E',
          border: '1px solid rgba(90,56,253,0.28)', position: 'relative', overflow: 'hidden',
          boxShadow: '0 0 28px rgba(90,56,253,0.18)' }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: 18, height: 18, background: '#141428', clipPath: 'polygon(100% 0, 0 100%, 100% 100%)' }} />
          {[28, 42, 56, 70].map((top, i) => (
            <div key={i} style={{ position: 'absolute', left: 10, top, height: 4, borderRadius: 2,
              background: i === stage % 4 ? BRAND_L : 'rgba(255,255,255,0.07)',
              width: i === 3 ? '45%' : i % 2 === 0 ? '70%' : '85%',
              transition: 'background 0.4s ease' }} />
          ))}
          <div style={{ position: 'absolute', left: 0, right: 0, height: 2,
            background: `linear-gradient(90deg, transparent, ${BRAND}, transparent)`,
            boxShadow: `0 0 8px rgba(90,56,253,0.6)`,
            animation: 'scanDoc 1.6s ease-in-out infinite' }} />
        </div>
        <div style={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: '50%',
          background: `linear-gradient(135deg, ${BRAND}, ${BRAND_2})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 10px rgba(90,56,253,0.55)',
          animation: 'caOrbit 1.6s ease-in-out infinite' }}>
          <Icons.Search style={{ width: 10, height: 10, color: '#fff' }} />
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontWeight: 700, color: '#fff', fontSize: '1rem', margin: '0 0 6px' }}>Analyzing Your Contract</p>
        <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.38)', margin: '0 0 16px', minHeight: '1.4em' }}>{stages[stage]}</p>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          {stages.map((_, i) => (
            <div key={i} style={{ width: i === stage ? 18 : 6, height: 6, borderRadius: 3,
              background: i <= stage ? BRAND_L : 'rgba(255,255,255,0.1)', transition: 'all 400ms ease' }} />
          ))}
        </div>
      </div>
      <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', fontFamily: MONO, margin: 0 }}>This usually takes 30–60 seconds</p>
    </div>
  );
}

// ─── Earnings modal ───────────────────────────────────────────────────────────
function EarningsModal({ analysis, dealTitle, onClose, onSubmit }) {
  const amount = _extractAmount(analysis?.summary, analysis?.key_terms);
  const [form, setForm] = useState({
    company_name: dealTitle?.split(/[-_\s]/)[0]?.trim() || '',
    deal_value:   amount ? String(amount) : '',
    deal_date:    new Date().toISOString().slice(0, 10),
    description:  `${dealTitle || 'NIL Contract'} — Safety Score: ${analysis?.safety_score ?? '—'}/100`,
    status:       'active',
  });
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }} transition={{ duration: 0.25, ease: EASE }}
        style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', pointerEvents: 'none' }}>
        <div onClick={e => e.stopPropagation()} style={{ pointerEvents: 'all', background: '#0F0F17', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 440, padding: '24px', boxShadow: '0 24px 80px rgba(0,0,0,0.85), 0 0 0 1px rgba(90,56,253,0.1)', position: 'relative', overflow: 'hidden', fontFamily: SANS }}>
          {/* Top hairline */}
          <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: 1, background: `linear-gradient(90deg, transparent, rgba(90,56,253,0.6), transparent)` }} />
          {/* Grain */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.035, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none', borderRadius: 20 }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h3 style={{ fontWeight: 700, fontSize: '1rem', color: '#fff', margin: 0 }}>Log to Earnings</h3>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.28)', margin: '3px 0 0' }}>Review and confirm before logging</p>
              </div>
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {[
                { label: 'Brand / Company', key: 'company_name', placeholder: 'Nike, Adidas…', type: 'text' },
                { label: 'Deal Value ($)',   key: 'deal_value',   placeholder: '0',             type: 'number' },
                { label: 'Deal Date',        key: 'deal_date',    placeholder: '',              type: 'date' },
                { label: 'Description',      key: 'description',  placeholder: 'Notes…',        type: 'text' },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</label>
                  <input type={type} value={form[key]} onChange={set(key)} placeholder={placeholder}
                    style={INPUT} min={type === 'number' ? '0' : undefined}
                    onFocus={e => { e.target.style.borderColor = 'rgba(90,56,253,0.55)'; e.target.style.background = 'rgba(90,56,253,0.06)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.04)'; }} />
                </div>
              ))}
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Status</label>
                <select value={form.status} onChange={set('status')}
                  style={{ ...INPUT, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <BtnGhost onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>Cancel</BtnGhost>
              <BtnPrimary onClick={() => onSubmit(form)} style={{ flex: 1, justifyContent: 'center' }}>Log Deal</BtnPrimary>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function SectionCard({ icon: Icon, iconColor, title, badge, collapsed, onToggle, children }) {
  return (
    <div style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden', fontFamily: SANS }}>
      <button onClick={onToggle} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {Icon && <Icon style={{ width: 15, height: 15, color: iconColor || BRAND_L, flexShrink: 0 }} />}
          <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem', fontFamily: SANS }}>{title}</span>
          {badge !== undefined && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20, height: 20, borderRadius: 6, padding: '0 5px', background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.3)', fontSize: '0.62rem', fontWeight: 800, color: '#f87171', fontFamily: MONO }}>
              {badge}
            </span>
          )}
        </div>
        <motion.div animate={{ rotate: collapsed ? 0 : 180 }} transition={{ duration: 0.2 }}>
          <Icons.ChevronDown style={{ width: 16, height: 16, color: 'rgba(255,255,255,0.28)' }} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: EASE }}
            style={{ overflow: 'hidden' }}>
            <div style={{ padding: '0 18px 18px' }}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── ContractAnalyzer ─────────────────────────────────────────────────────────
const ContractAnalyzer = ({ setSuccess, setError, setActiveComponent }) => {
  const { apiCall } = useAuth();
  const [uploading, setUploading]             = useState(false);
  const [analysis, setAnalysis]               = useState(null);
  const [contracts, setContracts]             = useState([]);
  const [dealTitle, setDealTitle]             = useState('');
  const [showHistory, setShowHistory]         = useState(false);
  const [deletingAll, setDeletingAll]         = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [confirmModal, setConfirmModal]       = useState(null);
  const [isDragOver, setIsDragOver]           = useState(false);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [expanded, setExpanded] = useState({ keyTerms: false, redFlags: false, recommendations: false, scoreBreakdown: false });
  const [showIntakeBanner, setShowIntakeBanner] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const r = await apiCall('/api/contracts/history');
      if (r?.contracts) setContracts(r.contracts);
    } catch { setContracts([]); }
  };

  const sanitizeUrl = (url) => (url || '').replace(/\/+$/, '');

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!dealTitle.trim()) { setError?.('Please enter a deal title first'); return; }
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload({ target: { files: [file], value: '' } });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.pdf') && !lower.endsWith('.docx')) { setError?.('Please upload a PDF or DOCX file'); return; }
    if (!dealTitle.trim()) { setError?.('Please enter a deal title first'); return; }
    if (file.size > 10 * 1024 * 1024) { setError?.('File must be under 10MB'); return; }

    let blob;
    try { blob = await file.arrayBuffer(); }
    catch { setError?.('Failed to read file. Please try again.'); return; }

    const freshen = () => new File([blob], file.name, { type: file.type });
    const postOnce = async (f) => {
      const fd = new FormData();
      fd.append('file', f, f.name);
      fd.append('deal_title', dealTitle.trim());
      const token = localStorage.getItem('token');
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 60000);
      try {
        const res = await fetch(`${sanitizeUrl(backendUrl)}/api/contracts/upload`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd, cache: 'no-store', signal: ctrl.signal,
        });
        clearTimeout(tid);
        return res;
      } catch (err) {
        clearTimeout(tid);
        if (err.name === 'AbortError') throw new Error('Upload timed out. Try a smaller file or check your connection.');
        throw err;
      }
    };

    setUploading(true);
    try {
      let response;
      for (let i = 1; i <= 3; i++) {
        try { response = await postOnce(freshen()); break; }
        catch (err) {
          const m = (err.message || '').toLowerCase();
          if ((m.includes('body stream') || m.includes('network') || m.includes('timeout')) && i < 3) {
            await new Promise(r => setTimeout(r, i * 500));
          } else throw err;
        }
      }

      if (!response.ok) {
        let ed = {};
        try { ed = await response.json(); } catch { try { ed = { detail: await response.text() }; } catch {} }
        if (response.status === 403 && (ed.detail?.includes('limit') || ed.detail?.includes('usage'))) {
          setShowUpgradePrompt(true);
          throw new Error("You've reached your contract analysis limit.");
        }
        throw new Error(ed.detail || `Upload failed: ${response.status}`);
      }

      const result = await response.json();
      setAnalysis(result.analysis);
      setExpanded({ keyTerms: false, redFlags: true, recommendations: false, scoreBreakdown: false });
      setSummaryCollapsed(false);

      if (result.contract_id) {
        try {
          const fresh = await apiCall(`/api/contracts/${result.contract_id}`);
          if (fresh?.analysis) setAnalysis(fresh.analysis);
        } catch {}
      }

      setShowIntakeBanner(true);
      window.dispatchEvent(new CustomEvent('contract:analyzed'));
      setDealTitle('');
      if (e.target) e.target.value = '';
      await loadHistory();
    } catch (err) {
      const m = String(err?.message || '').toLowerCase();
      if (err.status === 403 || m.includes('limit')) {
        setShowUpgradePrompt(true);
        setError?.("You've reached your contract analysis limit. Upgrade to continue.");
      } else {
        setError?.(err.message || 'Failed to analyze contract. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleContractClick = async (id) => {
    if (!id || id === 'undefined') { setError?.('Missing contract ID.'); return; }
    try {
      const r = await apiCall(`/api/contracts/${id}`);
      if (r?.analysis) {
        setAnalysis(r.analysis);
        setShowHistory(false);
        setExpanded({ keyTerms: false, redFlags: true, recommendations: false, scoreBreakdown: false });
        setSummaryCollapsed(false);
        const title = r.contract?.deal_title || '';
        setDealTitle(title);
      }
    } catch { setError?.('Failed to load contract.'); }
  };

  const handleLogToEarnings = async (form) => {
    try {
      const r = await apiCall('/api/earnings/add-deal', {
        method: 'POST',
        body: JSON.stringify({
          company_name: form.company_name || 'Contract Deal',
          deal_value:   parseFloat(form.deal_value) || 0,
          deal_date:    form.deal_date,
          description:  form.description,
          status:       form.status,
        }),
      });
      if (r?.success || r?.deal_id || r?.id) {
        setSuccess?.('Logged to Earnings Tracker');
        setShowEarningsModal(false);
      } else {
        setError?.('Failed to log to earnings.');
      }
    } catch (e) {
      setError?.(e.message || 'Failed to log to earnings.');
    }
  };

  const sc = analysis ? scoreColor(analysis.safety_score) : null;

  const expiresAt = analysis?.expires_at ? new Date(analysis.expires_at) : null;
  const expiresSoon = expiresAt && expiresAt < new Date(Date.now() + 30 * 864e5);
  const expired = expiresAt && expiresAt < new Date();

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '20px 16px 80px', maxWidth: '100%', fontFamily: SANS }}>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: EASE }}
        style={{ background: 'radial-gradient(ellipse 160% 130% at 80% 0%, rgba(90,56,253,0.32) 0%, rgba(90,56,253,0.14) 30%, transparent 65%), #0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '20px 20px 18px', marginBottom: 14, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(90,56,253,0.5), transparent)', zIndex: 1 }} />
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none', borderRadius: 18 }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(90,56,253,0.16)', border: '1px solid rgba(90,56,253,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icons.Contract style={{ width: 20, height: 20, color: BRAND_L }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ fontWeight: 800, fontSize: '1.2rem', color: '#fff', margin: 0, letterSpacing: '-0.025em' }}>Contract Analyzer</h1>
              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', margin: '2px 0 0' }}>AI-powered deal review with Deal Safety Score™</p>
            </div>
            <span style={{ fontSize: '0.58rem', fontFamily: MONO, fontWeight: 700, color: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap', letterSpacing: '0.04em', flexShrink: 0 }}>
              PATENT PENDING
            </span>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <BtnPrimary onClick={() => { setAnalysis(null); setDealTitle(''); }}>
              <Icons.Plus style={{ width: 14, height: 14 }} />
              New Analysis
            </BtnPrimary>
            <BtnGhost onClick={() => setShowHistory(h => !h)}>
              <Icons.Clock style={{ width: 14, height: 14 }} />
              History {contracts.length > 0 && `(${contracts.length})`}
            </BtnGhost>
          </div>
        </div>
      </motion.div>

      {/* ── Upload / Analyzing ────────────────────────────────────────────── */}
      {(!analysis || uploading) && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: EASE }}
          style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '20px', marginBottom: 14, overflow: 'hidden' }}>
          {uploading ? <AnalyzingState /> : (
            <div>
              <h2 style={{ fontWeight: 700, color: '#fff', margin: '0 0 20px', fontSize: '0.95rem' }}>Upload a Contract</h2>

              {/* Step 1 — Deal Title */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(90,56,253,0.18)', border: '1px solid rgba(90,56,253,0.32)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 800, color: BRAND_L, flexShrink: 0 }}>1</span>
                  Deal Title
                </label>
                <input type="text" value={dealTitle} onChange={e => setDealTitle(e.target.value)}
                  placeholder="e.g. Nike Social Media Campaign"
                  style={INPUT}
                  onFocus={e => { e.target.style.borderColor = 'rgba(90,56,253,0.55)'; e.target.style.background = 'rgba(90,56,253,0.06)'; e.target.style.boxShadow = '0 0 0 3px rgba(90,56,253,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.04)'; e.target.style.boxShadow = 'none'; }} />
                <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', margin: '5px 0 0', fontFamily: MONO }}>Helps you identify this contract in history</p>
              </div>

              {/* Step 2 — Upload */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.8rem', fontWeight: 600, color: dealTitle.trim() ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.2)', marginBottom: 8 }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%',
                    background: dealTitle.trim() ? 'rgba(90,56,253,0.18)' : 'rgba(255,255,255,0.05)',
                    border: dealTitle.trim() ? '1px solid rgba(90,56,253,0.32)' : '1px solid rgba(255,255,255,0.08)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.62rem', fontWeight: 800, color: dealTitle.trim() ? BRAND_L : 'rgba(255,255,255,0.2)', flexShrink: 0 }}>2</span>
                  Upload File
                </label>
                <div
                  style={{ border: `2px dashed ${isDragOver ? 'rgba(90,56,253,0.75)' : dealTitle.trim() ? 'rgba(90,56,253,0.38)' : 'rgba(255,255,255,0.07)'}`,
                    borderRadius: 12, padding: '32px 24px', textAlign: 'center',
                    transition: 'border-color 180ms, background 180ms, opacity 200ms',
                    opacity: dealTitle.trim() ? 1 : 0.45,
                    cursor: dealTitle.trim() ? 'pointer' : 'not-allowed',
                    background: isDragOver ? 'rgba(90,56,253,0.07)' : dealTitle.trim() ? 'rgba(90,56,253,0.03)' : 'transparent' }}
                  onClick={() => dealTitle.trim() && fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); if (dealTitle.trim()) setIsDragOver(true); }}
                  onDragEnter={e => { e.preventDefault(); if (dealTitle.trim()) setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDrop}>
                  <input ref={fileInputRef} type="file" accept=".pdf,.docx" onChange={handleFileUpload} style={{ display: 'none' }} disabled={!dealTitle.trim()} />
                  <div style={{ width: 44, height: 44, borderRadius: 10, margin: '0 auto 12px',
                    background: dealTitle.trim() ? 'rgba(90,56,253,0.14)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${dealTitle.trim() ? 'rgba(90,56,253,0.28)' : 'rgba(255,255,255,0.07)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icons.Upload style={{ width: 20, height: 20, color: dealTitle.trim() ? BRAND_L : 'rgba(255,255,255,0.2)' }} />
                  </div>
                  {dealTitle.trim() ? (
                    <>
                      <p style={{ fontWeight: 600, color: '#fff', margin: '0 0 4px', fontSize: '0.875rem' }}>Drop your contract or click to browse</p>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', margin: 0 }}>PDF or DOCX · max 10MB</p>
                    </>
                  ) : (
                    <p style={{ fontWeight: 500, color: 'rgba(255,255,255,0.25)', fontSize: '0.875rem', margin: 0 }}>Enter a deal title above to unlock upload</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ── Analysis Results ──────────────────────────────────────────────── */}
      {analysis && !uploading && (
        <motion.div
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          initial="hidden" animate="show"
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Ghost Agent intake banner */}
          {showIntakeBanner && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 340, damping: 28 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '11px 15px',
                background: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: BRAND_L, flexShrink: 0, boxShadow: '0 0 6px rgba(167,139,250,0.6)' }} />
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.45 }}>
                  <span style={{ color: BRAND_L, fontWeight: 600 }}>Ghost Agent</span> is preparing your negotiation strategy.
                </p>
              </div>
              <button onClick={() => setShowIntakeBanner(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '1.1rem', lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>×</button>
            </motion.div>
          )}

          {/* Deal Safety Score hero */}
          <motion.div
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 320, damping: 28 } } }}
            style={{ background: '#0F0F17', border: `1px solid ${sc.border}`, borderRadius: 18, padding: '20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, opacity: 0.04, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none', borderRadius: 18 }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                <ScoreRing score={analysis.safety_score} color={sc.text} />
                <div style={{ flex: 1, minWidth: 160 }}>
                  <p style={{ fontSize: '0.62rem', fontFamily: MONO, fontWeight: 700, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>Deal Safety Score™</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: sc.text, letterSpacing: '-0.03em' }}>{sc.label}</span>
                    <span style={{ padding: '3px 10px', borderRadius: 8, background: sc.bg, border: `1px solid ${sc.border}`, fontSize: '0.68rem', fontWeight: 700, color: sc.text, fontFamily: MONO }}>
                      {analysis.safety_score}/100
                    </span>
                  </div>
                  {/* Score bar */}
                  <div style={{ height: 5, borderRadius: 5, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                    <motion.div style={{ height: '100%', borderRadius: 5, background: `linear-gradient(90deg, ${sc.text}88, ${sc.text})` }}
                      initial={{ width: 0 }} animate={{ width: `${Math.min(analysis.safety_score ?? 0, 100)}%` }}
                      transition={{ duration: 1, ease: EASE, delay: 0.3 }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: 'rgba(255,255,255,0.18)', marginTop: 3, fontFamily: MONO }}>
                    {['0', '25', '50', '75', '100'].map(n => <span key={n}>{n}</span>)}
                  </div>
                </div>
              </div>

              {/* Expiry alert */}
              {expiresAt && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 10, marginTop: 14,
                  background: expired ? 'rgba(239,68,68,0.07)' : expiresSoon ? 'rgba(245,158,11,0.07)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${expired ? 'rgba(239,68,68,0.2)' : expiresSoon ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.07)'}` }}>
                  <Icons.Clock style={{ width: 14, height: 14, color: expired ? '#f87171' : expiresSoon ? '#fbbf24' : 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                    Contract expires{' '}
                    <strong style={{ color: expired ? '#f87171' : expiresSoon ? '#fbbf24' : 'rgba(255,255,255,0.7)' }}>{fmt(analysis.expires_at)}</strong>
                    {expired && ' — expired'}{expiresSoon && !expired && ' — expiring soon'}
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Executive Summary */}
          {analysis.summary && (
            <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } } }}>
              <SectionCard icon={Icons.Star} iconColor={BRAND_L} title="Executive Summary" collapsed={summaryCollapsed} onToggle={() => setSummaryCollapsed(v => !v)}>
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.65, margin: 0 }}>
                  {typeof analysis.summary === 'string' ? analysis.summary : 'Analysis complete — review key terms and red flags below.'}
                </p>
              </SectionCard>
            </motion.div>
          )}

          {/* Expand / Collapse All */}
          <motion.div variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 0' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', fontFamily: MONO, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Detailed Analysis</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setExpanded({ keyTerms: true, redFlags: true, recommendations: true, scoreBreakdown: true })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: BRAND_L, padding: '4px 0', fontFamily: SANS }}>Expand all</button>
              <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.75rem' }}>·</span>
              <button onClick={() => setExpanded({ keyTerms: false, redFlags: false, recommendations: false, scoreBreakdown: false })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', padding: '4px 0', fontFamily: SANS }}>Collapse all</button>
            </div>
          </motion.div>

          {/* Detail sections — default collapsed */}
          {analysis.red_flags?.length > 0 && (
            <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } } }}>
              <SectionCard icon={Icons.Warning} iconColor="#f87171" title="Red Flags" badge={analysis.red_flags.length}
                collapsed={!expanded.redFlags} onToggle={() => setExpanded(p => ({ ...p, redFlags: !p.redFlags }))}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {analysis.red_flags.map((flag, i) => {
                    const issue = typeof flag === 'string' ? flag : flag?.issue || 'Potential Issue';
                    const desc  = typeof flag === 'string' ? '' : flag?.description || '';
                    return (
                      <div key={i} style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.18)', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontWeight: 700, color: '#f87171', fontSize: '0.82rem', marginBottom: desc ? 3 : 0 }}>{issue}</div>
                        {desc && <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>{desc}</div>}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            </motion.div>
          )}

          {analysis.key_terms?.length > 0 && (
            <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } } }}>
              <SectionCard icon={Icons.Contract} iconColor={BRAND_L} title="Key Terms"
                collapsed={!expanded.keyTerms} onToggle={() => setExpanded(p => ({ ...p, keyTerms: !p.keyTerms }))}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {analysis.key_terms.map((term, i) => (
                    <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.82rem', marginBottom: 3 }}>{term.term}</div>
                      <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>{term.description}</div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </motion.div>
          )}

          <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } } }}>
            <SectionCard icon={Icons.Star} iconColor={BRAND_L} title="Recommendations"
              collapsed={!expanded.recommendations} onToggle={() => setExpanded(p => ({ ...p, recommendations: !p.recommendations }))}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(analysis.recommendations?.length > 0 ? analysis.recommendations : [{ category: 'Legal Review', suggestion: 'Have a qualified attorney review this contract before signing.' }]).map((rec, i) => (
                  <div key={i} style={{ background: 'rgba(90,56,253,0.07)', border: '1px solid rgba(90,56,253,0.18)', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontWeight: 700, color: BRAND_L, fontSize: '0.82rem', marginBottom: 3 }}>{rec.category || 'Recommendation'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>{rec.suggestion || rec}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </motion.div>

          {analysis.score_breakdown && (
            <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } } }}>
              <SectionCard icon={Icons.TrendUp} iconColor={BRAND_L} title="Score Breakdown"
                collapsed={!expanded.scoreBreakdown} onToggle={() => setExpanded(p => ({ ...p, scoreBreakdown: !p.scoreBreakdown }))}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {Object.entries(analysis.score_breakdown).map(([cat, score]) => {
                    const c = scoreColor(score);
                    return (
                      <div key={cat}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                          <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', textTransform: 'capitalize' }}>{cat.replace(/_/g, ' ')}</span>
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: c.text, fontFamily: MONO }}>{score}/100</span>
                        </div>
                        <div style={{ height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                          <motion.div style={{ height: '100%', borderRadius: 4, background: c.text }}
                            initial={{ width: 0 }} animate={{ width: `${score}%` }} transition={{ duration: 0.7, ease: EASE }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            </motion.div>
          )}

          {/* Action bar */}
          <motion.div variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } } }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
              background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderTop: `2px solid rgba(90,56,253,0.4)`,
              borderRadius: 14, padding: '16px 20px' }}>
            <div>
              <p style={{ fontWeight: 700, color: '#fff', fontSize: '0.875rem', margin: '0 0 2px' }}>Track This Contract</p>
              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.3)', margin: 0 }}>Log to earnings and get AI coaching from Ghost Agent</p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <BtnGhost onClick={() => setShowEarningsModal(true)}>
                <Icons.TrendUp style={{ width: 14, height: 14 }} />
                Log to Earnings
              </BtnGhost>
              <BtnPrimary onClick={() => { _setGhostContext(analysis, dealTitle); setActiveComponent?.('ghost'); }}>
                <Icons.Ghost style={{ width: 14, height: 14 }} />
                Ask Ghost Agent
              </BtnPrimary>
            </div>
          </motion.div>

          <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.18)', lineHeight: 1.5, fontFamily: MONO, margin: 0 }}>
            This tool does not provide legal advice. All outputs are intelligent recommendations sourced from NCAA guidance. Consult a qualified professional before making final decisions.
          </p>
        </motion.div>
      )}

      {/* ── Contract History ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {showHistory && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: EASE }}
            style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '20px', marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', margin: 0 }}>Contract History</h2>
              {contracts.length > 0 && (
                <BtnGhost danger disabled={deletingAll} onClick={() => setConfirmModal({
                  title: 'Clear All History',
                  message: 'This will permanently delete all analyzed contracts. This cannot be undone.',
                  confirmLabel: 'Clear All', danger: true,
                  onConfirm: async () => {
                    setDeletingAll(true);
                    try {
                      await apiCall('/api/contracts/history', { method: 'DELETE' });
                      setContracts([]); setShowHistory(false); setSuccess?.('Contract history cleared');
                    } catch { setError?.('Failed to clear contract history'); }
                    finally { setDeletingAll(false); }
                  },
                })}>
                  {deletingAll ? 'Deleting…' : 'Clear All'}
                </BtnGhost>
              )}
            </div>

            {contracts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Icons.Contract style={{ width: 36, height: 36, color: 'rgba(255,255,255,0.15)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.875rem', margin: 0 }}>No contracts analyzed yet</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto' }}>
                {contracts.map((c) => {
                  const cc = scoreColor(c.safety_score);
                  return (
                    <div key={c.contract_id} onClick={() => handleContractClick(c.contract_id)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 12, padding: '11px 14px', cursor: 'pointer', transition: 'background 160ms' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 600, color: '#fff', fontSize: '0.875rem', margin: '0 0 2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.deal_title}</p>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: MONO }}>
                          {c.filename} · {fmt(c.created_at)}
                          {c.expires_at && (
                            <span style={{ marginLeft: 8, color: new Date(c.expires_at) < new Date(Date.now() + 30 * 864e5) ? '#f87171' : 'rgba(255,255,255,0.22)' }}>
                              · expires {fmt(c.expires_at)}
                            </span>
                          )}
                        </p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {c.safety_score && (
                          <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, background: cc.bg, border: `1px solid ${cc.border}`, color: cc.text, fontFamily: MONO }}>
                            {c.safety_score}/100
                          </span>
                        )}
                        <button onClick={e => { e.stopPropagation(); setConfirmModal({
                          title: 'Delete Contract', message: `Delete "${c.deal_title}"? This cannot be undone.`,
                          confirmLabel: 'Delete', danger: true,
                          onConfirm: async () => {
                            try {
                              const r = await apiCall(`/api/contracts/${c.contract_id}`, { method: 'DELETE' });
                              if (r?.success) { setContracts(prev => prev.filter(x => x.contract_id !== c.contract_id)); setSuccess?.('Contract deleted'); }
                            } catch { setError?.('Failed to delete contract'); }
                          },
                        }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                          <Icons.Trash style={{ width: 15, height: 15, color: '#f87171', opacity: 0.65 }} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Earnings Modal ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showEarningsModal && (
          <EarningsModal analysis={analysis} dealTitle={dealTitle} onClose={() => setShowEarningsModal(false)} onSubmit={handleLogToEarnings} />
        )}
      </AnimatePresence>

      {/* ── Confirm modal ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {confirmModal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
              onClick={() => setConfirmModal(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ duration: 0.22, ease: EASE }}
              style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', pointerEvents: 'none' }}>
              <div onClick={e => e.stopPropagation()} style={{ pointerEvents: 'all', background: '#0F0F17', border: `1px solid ${confirmModal.danger ? 'rgba(239,68,68,0.2)' : 'rgba(90,56,253,0.2)'}`, borderRadius: 20, width: '100%', maxWidth: 380, padding: '28px 24px', textAlign: 'center', boxShadow: '0 24px 80px rgba(0,0,0,0.85)', position: 'relative', overflow: 'hidden', fontFamily: SANS }}>
                <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: 1, background: `linear-gradient(90deg, transparent, ${confirmModal.danger ? 'rgba(239,68,68,0.5)' : 'rgba(90,56,253,0.5)'}, transparent)` }} />
                <div style={{ width: 48, height: 48, borderRadius: '50%', margin: '0 auto 16px', background: confirmModal.danger ? 'rgba(239,68,68,0.1)' : 'rgba(90,56,253,0.1)', border: `1px solid ${confirmModal.danger ? 'rgba(239,68,68,0.25)' : 'rgba(90,56,253,0.25)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icons.Warning style={{ width: 20, height: 20, color: confirmModal.danger ? '#f87171' : BRAND_L }} />
                </div>
                <h2 style={{ fontWeight: 700, color: '#fff', fontSize: '1rem', margin: '0 0 8px' }}>{confirmModal.title}</h2>
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.4)', margin: '0 0 24px', lineHeight: 1.6 }}>{confirmModal.message}</p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <BtnGhost onClick={() => setConfirmModal(null)} style={{ flex: 1, justifyContent: 'center' }}>Cancel</BtnGhost>
                  <motion.button whileTap={{ scale: 0.97 }} onClick={async () => { setConfirmModal(null); await confirmModal.onConfirm(); }}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 10, fontWeight: 700, fontSize: '0.875rem', border: 'none',
                      background: confirmModal.danger ? 'linear-gradient(135deg, #dc2626, #b91c1c)' : `linear-gradient(135deg, ${BRAND}, ${BRAND_2})`,
                      color: '#fff', cursor: 'pointer', fontFamily: SANS,
                      boxShadow: confirmModal.danger ? '0 4px 14px rgba(220,38,38,0.3)' : '0 4px 14px rgba(90,56,253,0.35)' }}>
                    {confirmModal.confirmLabel || 'Confirm'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Upgrade prompt ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showUpgradePrompt && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
              onClick={() => setShowUpgradePrompt(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.22, ease: EASE }}
              style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', pointerEvents: 'none' }}>
              <div onClick={e => e.stopPropagation()} style={{ pointerEvents: 'all', background: '#0F0F17', border: '1px solid rgba(90,56,253,0.2)', borderRadius: 20, width: '100%', maxWidth: 400, padding: '28px 24px', textAlign: 'center', boxShadow: '0 24px 80px rgba(0,0,0,0.85)', position: 'relative', overflow: 'hidden', fontFamily: SANS }}>
                <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(90,56,253,0.6), transparent)' }} />
                <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 20px', background: 'rgba(90,56,253,0.12)', border: '1px solid rgba(90,56,253,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icons.Lock style={{ width: 26, height: 26, color: BRAND_L }} />
                </div>
                <h2 style={{ fontWeight: 800, color: '#fff', fontSize: '1.1rem', margin: '0 0 10px' }}>Usage Limit Reached</h2>
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.4)', margin: '0 0 24px', lineHeight: 1.6 }}>
                  You've reached your contract analysis limit. Upgrade to Pro or Elite for more analyses and unlimited history.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <BtnGhost onClick={() => setShowUpgradePrompt(false)} style={{ flex: 1, justifyContent: 'center' }}>View History</BtnGhost>
                  <BtnPrimary onClick={() => { setShowUpgradePrompt(false); setShowUpgradeModal(true); }} style={{ flex: 1, justifyContent: 'center' }}>Upgrade Now</BtnPrimary>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <SubscriptionUpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} currentTier="free" />
    </div>
  );
};

export default ContractAnalyzer;
